import React from "react";
import styled, { keyframes } from "styled-components";
import { Container } from "../components/ui/Container";
import { ExternalButton, LinkButton } from "../components/ui/Button";
import { colors, ease, fonts, media } from "../theme";

export const Hero = () => {
  return (
    <Wrap>
      <Glow />
      <Container>
        <Grid>
          <Left>
            <Pill>
              <Dot /> Live in Uganda · Licensed digital lender
            </Pill>

            <Title>
              Affordable digital credit.
              <br />
              <em>Real returns. Real impact.</em>
            </Title>

            <Sub>
              Everpesa is the peer to peer marketplace connecting everyday investors
              with small businesses that need working capital. No land titles. No bank
              history. Apply or invest in minutes from a phone.
            </Sub>

            <Ctas>
              <ExternalButton
                href="https://invest.everpesa.com"
                target="_blank"
                rel="noopener noreferrer"
                variant="white"
              >
                Start investing
                <Arrow />
              </ExternalButton>
              <LinkButton variant="outlineWhite" to="/borrow">
                Apply for a loan
              </LinkButton>
            </Ctas>

            <Quick>
              <QStat>
                <strong>Up to 7.6%</strong>
                <span>typical investor returns per cycle</span>
              </QStat>
              <QSep />
              <QStat>
                <strong>UGX 10,000</strong>
                <span>minimum investment</span>
              </QStat>
              <QSep />
              <QStat>
                <strong>98%</strong>
                <span>repayment rate</span>
              </QStat>
            </Quick>
          </Left>

          <Right>
            <PhotoWrap>
              <PhotoMain src={process.env.PUBLIC_URL + "/images/shopkeeper-portrait.jpg"} alt="Hardware shopkeeper in Kampala" />
              <FloatCard top="6%" left="-28px" delay="0s">
                <CardLabel>Live</CardLabel>
                <CardTitle>Hardware stock loan</CardTitle>
                <CardRow><span>Funding</span><strong>65%</strong></CardRow>
                <Bar><span /></Bar>
                <CardRow><span>Tenor</span><strong>60 days</strong></CardRow>
                <CardRow $accent><span>Net return</span><strong>~9%</strong></CardRow>
              </FloatCard>
              <FloatCard bottom="-22px" right="-20px" delay="0.6s" $small>
                <CardLabel $green>Repaid</CardLabel>
                <CardTitle>Salon expansion in Wakiso</CardTitle>
                <CardRow><span>Principal</span><strong>UGX 1,000,000</strong></CardRow>
                <CardRow $accent><span>Earned</span><strong>+UGX 95,000</strong></CardRow>
              </FloatCard>
            </PhotoWrap>
          </Right>
        </Grid>
      </Container>

      <Curve viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden>
        <path d="M0,120 C300,40 600,0 1440,40 L1440,120 Z" fill="white" />
      </Curve>
    </Wrap>
  );
};

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const Wrap = styled.section`
  position: relative;
  background:
    radial-gradient(1200px 600px at 80% -10%, rgba(242,181,61,0.18), transparent 60%),
    radial-gradient(900px 600px at 0% 0%, rgba(0,51,204,0.0), transparent 60%),
    linear-gradient(180deg, ${colors.brand} 0%, ${colors.brandDark} 100%);
  color: white;
  padding: 56px 0 110px;
  overflow: hidden;

  ${media.md} { padding: 56px 0 110px; }
  ${media.sm} { padding: 40px 0 90px; }
`;

const Glow = styled.div`
  position: absolute;
  top: -250px;
  right: -250px;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,255,255,0.08), transparent 60%);
  pointer-events: none;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  align-items: center;
  gap: 48px;
  position: relative;
  z-index: 2;

  ${media.lg} { grid-template-columns: 1fr; gap: 48px; }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding: 8px 14px;
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.92);
  backdrop-filter: blur(8px);
`;

const pulse = keyframes`
  0%,100% { box-shadow: 0 0 0 0 rgba(21,165,114,0.7); }
  50% { box-shadow: 0 0 0 8px rgba(21,165,114,0); }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  background: ${colors.success};
  border-radius: 50%;
  animation: ${pulse} 2.2s ${ease} infinite;
`;

const Title = styled.h1`
  color: white;
  font-family: ${fonts.display};
  font-weight: 800;
  em { font-style: normal; color: ${colors.gold}; }
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.82);
  font-size: 17px;
  max-width: 540px;
  ${media.sm} { font-size: 15.5px; }
`;

const Ctas = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const Arrow = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  position: relative;
  &::before {
    content: "→";
    font-weight: 700;
  }
`;

const Quick = styled.div`
  margin-top: 28px;
  display: flex;
  align-items: stretch;
  gap: 22px;
  flex-wrap: wrap;
`;

const QStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong {
    font-family: ${fonts.display};
    font-size: 24px;
    font-weight: 700;
    color: white;
  }
  span {
    font-size: 13px;
    color: rgba(255,255,255,0.6);
  }
`;

const QSep = styled.span`
  width: 1px;
  background: rgba(255,255,255,0.18);
  align-self: stretch;
`;

const Right = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  ${media.lg} { padding: 0 8px; }
`;

const PhotoWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
`;

const PhotoMain = styled.img`
  width: 100%;
  border-radius: 28px;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  box-shadow: 0 40px 80px -20px rgba(0,0,0,0.45);
  border: 1px solid rgba(255,255,255,0.12);
`;

const FloatCard = styled.div`
  position: absolute;
  ${(p) => p.top && `top: ${p.top};`}
  ${(p) => p.bottom && `bottom: ${p.bottom};`}
  ${(p) => p.left && `left: ${p.left};`}
  ${(p) => p.right && `right: ${p.right};`}
  background: white;
  color: ${colors.ink};
  padding: 16px 18px;
  border-radius: 18px;
  width: ${(p) => (p.$small ? "230px" : "260px")};
  box-shadow: 0 24px 60px -16px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${float} 5s ${ease} infinite;
  animation-delay: ${(p) => p.delay || "0s"};

  ${media.md} { display: none; }
`;

const CardLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(p) => (p.$green ? colors.success : colors.brand)};
  &::before {
    content: "";
    width: 6px; height: 6px; border-radius: 50%;
    background: currentColor;
  }
`;

const CardTitle = styled.div`
  font-family: ${fonts.display};
  font-weight: 700;
  font-size: 15px;
  color: ${colors.ink};
`;

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12.5px;
  span { color: ${colors.inkSoft}; }
  strong { color: ${(p) => (p.$accent ? colors.success : colors.ink)}; font-weight: 600; }
`;

const Bar = styled.div`
  height: 6px;
  background: ${colors.brandSoft};
  border-radius: 999px;
  overflow: hidden;
  span {
    display: block;
    width: 65%;
    height: 100%;
    background: linear-gradient(90deg, ${colors.brand}, ${colors.gold});
    border-radius: 999px;
  }
`;

const Curve = styled.svg`
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 80px;
  display: block;
  ${media.sm} { height: 50px; }
`;
